import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetPublication } from '@utils/api/back-office/plans-bo/use-get-reseller-publications.tsx';
import { useGetResellerCustomers } from '@utils/api/back-office/plans-bo/use-get-reseller-customers.tsx';
import { useCreateResellerPublication } from '@utils/api/back-office/plans-bo/use-create-reseller-publication.tsx';
import { useDeleteResellerPublication } from '@utils/api/back-office/plans-bo/use-delete-reseller-from-publication.tsx';
import { queryClient } from '@utils/api/react-query-instance.ts';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import { LabelValueSelectInput } from '@utils/types/shared/label-value-select-input';
import { MultiValue, SingleValue } from 'react-select';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Trash2, AlertTriangle } from 'lucide-react';

type Params = {
  planId: string;
};

type ResellerRow = {
  id: string;
  name: string;
  reseller_id: string;
  isPublished: boolean;
};

export default function ResellerPublications() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const planId = useParams<Params>().planId!;

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedReseller, setSelectedReseller] =
    useState<SingleValue<LabelValueSelectInput>>(null);
  const [availableSearchQuery, setAvailableSearchQuery] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const {
    data: publications,
    isLoading: isPublicationsLoading,
    isError: isPublicationsError
  } = useGetPublication(Number(planId));
  const { data: availableResellers } =
    useGetResellerCustomers(availableSearchQuery);

  const { mutateAsync: createResellerPublication } =
    useCreateResellerPublication(planId?.toString());
  const { mutateAsync: deletePublication } = useDeleteResellerPublication(
    Number(planId)
  );

  const isLoading = isPublicationsLoading;
  const isError = isPublicationsError;

  const columns = useMemo<DataGridColumn<ResellerRow>[]>(
    () => [
      {
        id: 'reseller_id',
        header: t('reseller-id'),
        accessorKey: 'reseller_id'
      },
      { id: 'name', header: t('reseller-name'), accessorKey: 'name' }
    ],
    [t]
  );

  const handleRemovePublication = useCallback(
    (resellerId: string) => {
      const pub = publications?.result.find(
        p => p.reseller_id === Number(resellerId)
      );
      if (!pub) return;

      toast.loading(t('removing-publication'), { id: `del-${resellerId}` });

      deletePublication(pub.id, {
        onSuccess: () => {
          toast.success(t('reseller-publication-deleted-successfully'), {
            id: `del-${resellerId}`
          });
          queryClient.invalidateQueries(['publication', Number(planId)]);
        },
        onError: (err: unknown) => {
          const e = err as {
            response?: {
              data?: {
                error_description?: string;
                error?: string;
                message?: string;
              };
            };
          };
          toast.error(
            e?.response?.data?.error_description ||
              e?.response?.data?.error ||
              e?.response?.data?.message ||
              t('failed-to-delete-reseller-publication'),
            { id: `del-${resellerId}` }
          );
        }
      });
    },
    [publications, deletePublication, t, planId]
  );

  const actions = useMemo<GridAction<ResellerRow>[]>(
    () => [
      {
        id: 'remove',
        label: t('remove'),
        icon: <Trash2 className="h-4 w-4" />,
        intent: 'danger',
        run: row => handleRemovePublication(row.id)
      }
    ],
    [t, handleRemovePublication]
  );

  const handleResellerChange = (
    selectedOption:
      | SingleValue<LabelValueSelectInput>
      | MultiValue<LabelValueSelectInput>
  ) => {
    setSelectedReseller(selectedOption as SingleValue<LabelValueSelectInput>);
  };

  const handleAddReseller = () => {
    if (!selectedReseller?.value) return;
    setIsAdding(true);
    const resellerId = selectedReseller.value;

    toast.loading(t('publishing-plan'), { id: `pub-${resellerId}` });

    createResellerPublication(
      { reseller_id: Number(resellerId) },
      {
        onSuccess: () => {
          toast.success(t('reseller-publication-created-successfully'), {
            id: `pub-${resellerId}`
          });
          setShowAddModal(false);
          setSelectedReseller(null);
          setIsAdding(false);
          queryClient.invalidateQueries(['publication', Number(planId)]);
        },
        onError: (err: unknown) => {
          const e = err as {
            response?: { data?: { error_description?: string } };
          };
          toast.error(
            e?.response?.data?.error_description ??
              t('failed-to-create-reseller-publication'),
            { id: `pub-${resellerId}` }
          );
          setIsAdding(false);
        }
      }
    );
  };

  const publishedResellers: ResellerRow[] =
    publications?.result.map(pub => ({
      id: pub.reseller_id.toString(),
      name: pub.reseller_name,
      reseller_id: pub.reseller_id.toString(),
      isPublished: true
    })) || [];

  const availableResellerOptions: LabelValueSelectInput[] =
    availableResellers?.result
      .filter(
        reseller =>
          !publications?.result.some(
            pub => pub.reseller_id === Number(reseller.id)
          )
      )
      .map(reseller => ({
        label: reseller.name,
        value: reseller.id
      })) || [];

  const ADD_FORM_ID = 'add-reseller-form';

  return (
    <div className="bg-app flex-1 py-4 space-y-4 sm:space-y-6">
      {isError && (
        <div className="flex items-center gap-2 rounded-2xl bg-red-500/10 border border-red-500/25 px-4 py-3 text-sm text-red-600 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{t('failed-to-load-data')}</span>
        </div>
      )}
      <div
        className="rounded-2xl border border-hairline shadow-card p-4 sm:p-6"
        style={{
          backgroundColor: getColour('secondLayer'),
          borderColor: getColour('border'),
          color: getColour('normalText')
        }}
      >
        <div className="mb-4 flex justify-between items-center">
          <h2
            className="text-lg font-semibold"
            style={{ color: getColour('normalText') }}
          >
            {t('reseller-publications')}
          </h2>
          <Button
            variant="primary"
            size="medium"
            onClick={() => setShowAddModal(true)}
          >
            {t('add-reseller')}
          </Button>
        </div>

        <DataGridV2<ResellerRow>
          data={publishedResellers}
          columns={columns}
          loading={isLoading}
          enablePagination={false}
          enableSorting={false}
          enableFiltering={false}
          dense
          getRowId={row => row.id}
          actions={actions}
        />
      </div>

      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedReseller(null);
        }}
        size="small"
        title={t('add-reseller')}
        footerActions={[
          <Button
            onClick={() => {
              setShowAddModal(false);
              setSelectedReseller(null);
            }}
            label={t('cancel')}
            size={'small'}
            variant={'smoke'}
            key="cancel"
          />,
          <Button
            label={isAdding ? t('adding') : t('add')}
            size={'small'}
            variant={'primary'}
            type="submit"
            form={ADD_FORM_ID}
            disabled={!selectedReseller}
            key="add"
          />
        ]}
      >
        <form
          id={ADD_FORM_ID}
          onSubmit={e => {
            e.preventDefault();
            handleAddReseller();
          }}
        >
          <button type="submit" className="hidden" aria-hidden="true" />

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: getColour('normalText') }}
            >
              {t('select-reseller')}
            </label>
            <SelectInput
              options={availableResellerOptions}
              value={selectedReseller}
              onChange={handleResellerChange}
              placeholder={t('search-reseller-customers')}
              onInputChange={inputValue => setAvailableSearchQuery(inputValue)}
              menuPlacement="bottom"
              styles={{
                menu: base => ({ ...base, zIndex: 10000 }),
                menuPortal: base => ({ ...base, zIndex: 10000 })
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
        </form>
      </Modal>
    </div>
  );
}
